import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardHeader from '../components/DashboardHeader';
import { getToken } from '../lib/session';

const API_BASE = (process.env.REACT_APP_API || 'http://localhost:6955/api').replace(
  /\/+$/,
  ''
);

const plannerSteps = [
  { key: 'content', label: 'Generating content with Clikkle AI…' },
  { key: 'image', label: 'Generating image with Clikkle…' },
  { key: 'calendar', label: 'Drafting your Content…' },
];

function progressFromStep(step) {
  if (step <= 0) return 5;
  if (step === 1) return 30;
  if (step === 2) return 65;
  if (step === 3) return 90;
  return 100;
}

function cleanAndFormatContent(content) {
  // Remove markdown formatting (e.g., # for headings) and clean up
  let formattedContent = content
    .replace(/^# (.*?)$/gm, '<h1>$1</h1>')  // Convert # heading to <h1>
    .replace(/^## (.*?)$/gm, '<h2>$1</h2>') // Convert ## heading to <h2>
    .replace(/^### (.*?)$/gm, '<h3>$1</h3>'); // Convert ### heading to <h3>

  // Replace multiple newlines with a single <p> tag for proper paragraph spacing
  formattedContent = formattedContent
    .replace(/\n+/g, '</p><p>')  // Replace newlines with paragraph tags
    .replace(/<\/p><p>/g, '</p><p>');  // Ensure proper spacing between paragraphs

  // Wrap content in <p> tags to ensure it is formatted correctly
  formattedContent = `<p>${formattedContent}</p>`;

  return formattedContent;
}


export default function PlannerGeneration() {
  const [stage, setStage] = useState('Starting…');
  const [step, setStep] = useState(0);
  const [error, setError] = useState('');
  const [q] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        
        const scheduledDate =
          q.get('date') || new Date().toISOString().slice(0, 10);

        const token = getToken();
        if (!token) throw new Error('No valid token found.');

        const headers = {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        };

        // STEP 1 – content generation
        // STEP 1 – content generation
        setStep(1);
        setStage('Generating content with AI…');
        const aiRes = await fetch(`${API_BASE}/ai/generate-debug`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            brand: JSON.parse(localStorage.getItem('onboarding.brand') || '{}'),
            settings: JSON.parse(localStorage.getItem('onboarding.settings') || '{}'),
          }),
        });
        if (!aiRes.ok) throw new Error(`AI generate failed (${aiRes.status})`);

        const { text, title: aiTitle } = await aiRes.json();
        if (cancelled) return;

        const rawTopic = q.get('topic') || localStorage.getItem('onboarding.topic') || '';
        const topic = rawTopic && rawTopic.toLowerCase() !== 'untitled' ? rawTopic : '';

        const title = (aiTitle && aiTitle.trim()) || topic || 'Generated Post';

        const captionHtml = cleanAndFormatContent(text);


        // STEP 2 – image generation
        setStep(2);
        setStage('Generating cover image…');
        const imgRes = await fetch(`${API_BASE}/ai/image`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            title: title || topic,
            content: text || '',
          }),
        });

        if (!imgRes.ok) throw new Error('Image generation failed');
        const { imageUrl } = await imgRes.json();
        if (!imageUrl) throw new Error('Invalid image URL');

        // STEP 3 – Save Brand (if not saved already)
        // STEP 3 – Save Brand (always attempt once for debugging)
        setStep(3);
        setStage('Saving brand and draft…');

        try {
          const rawBrandId = localStorage.getItem('onboarding.brandId');
          console.log('[PlannerGeneration] stored onboarding.brandId:', rawBrandId);

          const rawBrandData = localStorage.getItem('onboarding.brand');
          console.log('[PlannerGeneration] raw onboarding.brand:', rawBrandData);

          let brandData = {};
          try {
            brandData = JSON.parse(rawBrandData || '{}');
          } catch (err) {
            console.error('[PlannerGeneration] Failed to parse brand JSON', err);
          }

          if (!brandData || !brandData.businessName) {
            console.error(
              '[PlannerGeneration] Brand data is invalid or missing – skipping brand save.',
              brandData
            );
          } else {
            console.log('[PlannerGeneration] Sending POST /api/brands with:', {
              name: brandData.businessName,
              website: brandData.website,
            });

            const res = await fetch(`${API_BASE}/brands`, {
              method: 'POST',
              headers,
              body: JSON.stringify({
                // userId not needed – backend uses req.user.id via requireAuth
                name: brandData.businessName || 'Default Brand',
                website: brandData.website || '',
                description: brandData.description || 'No description',
                logoUrl: brandData.logoPrimary || '',
                primaryColor: brandData.brandColors?.[0] || '#000000',
                secondaryColor: brandData.brandColors?.[1] || '#FFFFFF',
                metadata: brandData,
              }),
            });

            if (!res.ok) {
              const errText = await res.text();
              console.error('Failed to save brand:', res.status, errText);
              // DO NOT throw here, so the rest of the flow still works
            } else {
              const brand = await res.json();
              console.log('Brand created successfully:', brand);
              localStorage.setItem('onboarding.brandId', brand._id);
            }
          }
        } catch (err) {
          console.error('[PlannerGeneration] Unexpected error in brand save block:', err);
        }


        // STEP 4 – save draft
        setStep(4);
        setStage('Saving draft…');

        const saveRes = await fetch(`${API_BASE}/planner/generate`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            title,
            captionHtml,
            thumbUrl: imageUrl,
            scheduledFor: scheduledDate,
            meta: { topic },
          }),
        });
        if (!saveRes.ok) throw new Error(`Create failed (${saveRes.status})`);
        const { item } = await saveRes.json();
        if (cancelled) return;

        setStep(5);
        setStage('All done! Opening your calendar…');
        navigate(`/mycalendar?flash=saved&focus=${item._id}`, { replace: true });
      } catch (e) {
        if (cancelled) return;
        console.error(e);
        setError(e.message || 'Something went wrong.');
        setStage('Failed');
        setStep(0);
        setTimeout(() => navigate('/dashboard?genError=1', { replace: true }), 1200);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [q, navigate]);

  const progress = progressFromStep(step);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
      <DashboardHeader />
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-xl rounded-3xl bg-slate-900/80 border border-slate-700 shadow-2xl p-8 md:p-10">
          {/* progress bar */}
          <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden mb-8">
            <div
              className="h-full bg-indigo-500 transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-9 h-9 rounded-full border border-indigo-400/60 flex items-center justify-center animate-spin-slow">
              <div className="w-5 h-5 rounded-full bg-indigo-400/80 animate-ping" />
            </div>
            <h1 className="text-xl md:text-2xl font-semibold tracking-tight">
              Generating Content with Clikkle…
            </h1>
          </div>

          <p className="text-sm md:text-base text-slate-300 text-center mb-8">
            {stage}
          </p>

          <ol className="space-y-4 text-left">
            {plannerSteps.map((s, index) => {
              const idx = index + 1;
              const isDone = step > idx;
              const isActive = step === idx;

              return (
                <li
                  key={s.key}
                  className="flex items-center gap-3 text-sm md:text-base"
                >
                  <div
                    className={[
                      'flex items-center justify-center w-7 h-7 rounded-full border transition-colors',
                      isDone
                        ? 'bg-emerald-500 border-emerald-400'
                        : isActive
                        ? 'bg-indigo-500 border-indigo-400 animate-pulse'
                        : 'border-slate-600 bg-slate-800',
                    ].join(' ')}
                  >
                    {isDone ? '✓' : idx}
                  </div>
                  <span
                    className={
                      isDone
                        ? 'text-emerald-300'
                        : isActive
                        ? 'text-slate-50'
                        : 'text-slate-400'
                    }
                  >
                    {s.label}
                  </span>
                </li>
              );
            })}
          </ol>

          {error && (
            <p
              className="text-red-400 mt-6 text-xs md:text-sm text-center"
              role="alert"
            >
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
